/**
 * cachedRead.ts — stale-while-revalidate for console screen reads.
 *
 * Console screens were built as "blank until every read resolves": a screen
 * set one `loading` flag, awaited a Promise.all of its reads, and rendered a
 * spinner until the slowest one landed. That has two costs. The barrier means
 * one slow endpoint hides four fast ones, and because nothing is remembered,
 * navigating away and back pays the whole bill again from scratch.
 *
 * This hook inverts it. Each read is independent, the last good value is
 * remembered per-org, and a revisit renders that value on the first frame
 * while a fresh read confirms it in the background. Screens using it show
 * real content immediately and never flash a spinner over data they already
 * had.
 *
 * Deliberately not a generic data-fetching library — no dedupe across
 * components, no focus refetch, no gc. It does the one thing the console
 * needs, in a way that is easy to reason about.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/** How long a persisted value may be shown before it's considered too stale to
 *  paint. It is still replaced by the live read either way — this only bounds
 *  how old a first-frame value can be (a day-old "Calls today" would mislead). */
const MAX_AGE_MS = 5 * 60 * 1000;

const STORAGE_PREFIX = 'sk_read_';

interface Entry<T> {
  orgId: string;
  at: number;
  data: T;
}

// In-memory first (survives navigation within a session, costs no parsing),
// localStorage second (survives reload).
const memory = new Map<string, Entry<unknown>>();

function storageKey(key: string): string {
  return STORAGE_PREFIX + key;
}

function readCached<T>(key: string, orgId: string | null): T | undefined {
  if (!orgId) return undefined;

  const hit = memory.get(key) as Entry<T> | undefined;
  if (hit && hit.orgId === orgId && Date.now() - hit.at < MAX_AGE_MS) return hit.data;

  try {
    if (typeof localStorage === 'undefined') return undefined;
    const raw = localStorage.getItem(storageKey(key));
    if (!raw) return undefined;
    const entry = JSON.parse(raw) as Entry<T>;
    // Org guard: never paint one tenant's numbers into another's screen.
    if (entry?.orgId !== orgId) return undefined;
    if (Date.now() - entry.at >= MAX_AGE_MS) return undefined;
    memory.set(key, entry as Entry<unknown>);
    return entry.data;
  } catch {
    return undefined;
  }
}

function writeCached<T>(key: string, orgId: string, data: T): void {
  const entry: Entry<T> = { orgId, at: Date.now(), data };
  memory.set(key, entry as Entry<unknown>);
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(storageKey(key), JSON.stringify(entry));
  } catch {
    /* quota / disabled storage — the in-memory copy still serves this session */
  }
}

export interface CachedRead<T> {
  /** Last known value: cached on the first frame, live once the read lands. */
  data: T | undefined;
  /** True only when there is nothing to show yet — i.e. render a skeleton.
   *  A background refresh over cached data does NOT set this. */
  loading: boolean;
  /** True while a network read is in flight, cached data or not. */
  refreshing: boolean;
  /** Re-read now (used after a mutation). */
  refresh: () => void;
}

/**
 * @param key      stable cache key for this read (e.g. 'home.kpis')
 * @param orgId    active org; the cache is scoped to it and cleared on switch
 * @param fetcher  a voiceService read returning { data, live }
 */
export function useCachedRead<T>(
  key: string,
  orgId: string | null,
  fetcher: () => Promise<{ data: T; live: boolean }>,
): CachedRead<T> {
  const [data, setData] = useState<T | undefined>(() => readCached<T>(key, orgId));
  const [refreshing, setRefreshing] = useState(false);
  const [nonce, setNonce] = useState(0);

  // The fetcher is typically an inline arrow, so a new identity every render.
  // Holding it in a ref keeps it out of the effect deps — otherwise the read
  // would re-fire on every render.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    let alive = true;

    const cached = readCached<T>(key, orgId);
    setData(cached);
    if (!orgId) { setRefreshing(false); return; }

    setRefreshing(true);
    void (async () => {
      const res = await fetcherRef.current();
      if (!alive) return;
      // A failed read (live: false) must not clobber a good cached value with
      // the service layer's empty placeholder — that would blank a populated
      // screen on one transient error.
      if (res.live) {
        writeCached(key, orgId, res.data);
        setData(res.data);
      } else if (cached === undefined) {
        // Nothing cached and the read failed: show the truthful empty rather
        // than spinning forever.
        setData(res.data);
      }
      setRefreshing(false);
    })();

    return () => { alive = false; };
  }, [key, orgId, nonce]);

  const refresh = useCallback(() => { setNonce((n) => n + 1); }, []);

  return { data, loading: data === undefined, refreshing, refresh };
}
